/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Mon Mar 25 2024
 **/

import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { Column, Typography } from "@/frameworks/designStuff/components";
import { hoverScrollCss } from "@/frameworks/designStuff/utils";
import { Configure, ChronoUtils } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";

import CallLogItem from "./CallLogItem";
import { useGroupCall } from "@/frameworks/core/API";
import { hasCallGatewayClient } from "@/modules/phone/utils";

type CallHistoryProps = {
	callees?: WebServices.User.UserInfo[];
};

export default function CallHistory({ callees }: CallHistoryProps) {
	const groupCallAPI = useGroupCall();
	const calleeID = callees?.[0].userID;
	const callNumber = callees?.[0].callGatewayClient?.callNumber;
	const isCallGatewayCall = hasCallGatewayClient(callees?.[0] as SwaggerModels.CallUserResponseVO);

	const { data } = useQuery({
		queryKey: ["detail", isCallGatewayCall ? callNumber : calleeID],
		queryFn: async () => {
			if (!Configure.is_authenticated) {
				return null;
			}

			if (isCallGatewayCall) {
				return await groupCallAPI.between.execute({ callNumber });
			} else {
				return await groupCallAPI.between.execute({ targetUserId: calleeID });
			}
		},
		enabled: isCallGatewayCall ? Boolean(callNumber) : Boolean(calleeID),
		refetchOnMount: true
	});

	const getCallTime = (startTime: number, endTime: number, callType: "send" | "received") => {
		const startDateTime = dayjs(startTime);
		const endDateTime = dayjs(endTime);
		const diff = endDateTime.diff(startDateTime, "second");
		const durationInSeconds = diff > 0 ? diff : 0;
		const hours = Math.floor(durationInSeconds / 3600);
		const minutes = Math.floor((durationInSeconds % 3600) / 60);
		const seconds = durationInSeconds % 60;
		const callState = callType === "send" ? _T("발신") : _T("수신");
		let result = `${callState} `;
		if (startTime) {
			if (hours > 0) {
				result += _T("{{hours}}시간", { hours });
			}
			result += _T("{{minutes}}분 {{seconds}}초", { minutes, seconds });
		} else {
			result += _T("0초");
		}
		return result;
	};

	const groupCallsByDate = (calls: WebServices.GroupCall.Between.Data["content"]) => {
		const groupedCalls: Record<string, WebServices.GroupCall.Between.Data["content"]> = {};

		calls.forEach((call) => {
			const callDate = dayjs(call.startTime).startOf("day");

			const formattedDate = ChronoUtils.formatPolicyDateOnly(callDate.valueOf());
			if (!groupedCalls[formattedDate]) {
				groupedCalls[formattedDate] = [];
			}
			groupedCalls[formattedDate].push(call);
		});

		return groupedCalls;
	};

	const groupedCalls = data?.data?.content ? groupCallsByDate(data.data.content) : [];

	return (
		<>
			{data?.data?.content.length === 0 ? (
				<Column gap={12} style={{ height: "100%" }} horizonAlign="center" verticalAlign="center">
					<Typography typoColor="neutral_70_100" typoSize="Body3">
						{_T("최근 통화내역이 없습니다")}
					</Typography>
				</Column>
			) : (
				<Column gap={12} css={[{ height: "calc( 100vh - 555px )", marginTop: 20 }, hoverScrollCss()]}>
					{Object.entries(groupedCalls).map(
						([day, calls]) =>
							calls.length > 0 && (
								<div key={day}>
									<Typography typoSize="Title1_B" typoColor="neutral_70_100">
										{day}
									</Typography>
									{calls.map((item) => {
										const status: PushServices.Call.Status = (() => {
											if (item.status === "ENDED" && item.callerId === Configure.userID) {
												return "REQUESTED";
											} else if (item.status === "ENDED" && calleeID === Configure.userID) {
												return "ACCEPTED";
											} else if (item.status === "ENDED" && !item.startTime) {
												return "HANGUP_NO_RESPONSE";
											} else {
												return item.status;
											}
										})();

										const callReceiveTime = item.startTime
											? item.startTime || 0
											: item.endTime || 0;

										return (
											<CallLogItem
												key={item.callId}
												callType={status}
												callReceiveTime={ChronoUtils.timestampFollowCountry(callReceiveTime)}
												callTime={getCallTime(
													item.startTime || 0,
													item.endTime || 0,
													item.callerId === Configure.userID ? "send" : "received"
												)}
											/>
										);
									})}
								</div>
							)
					)}
				</Column>
			)}
		</>
	);
}
